import React from 'react';
import upperFirst from 'lodash/fp/upperFirst';
import { ArrayElementType } from '../../types/utils/ArrayElementType';
import Link, { LinkProps } from '../Link/Link';
import Element, { SupportedColor } from '../Element/Element';
import styles from './Logo.module.scss';

export const supportedLogoSizes = ['xs', 'sm', 'md', 'lg', 'xlg'] as const;
export type SupportedLogoSize = ArrayElementType<typeof supportedLogoSizes>;

export type LogoProps = {
  /** Icon size */
  size?: SupportedLogoSize;
  /** Logo color */
  color?: SupportedColor;
  /** Tag of wrapping element */
  tagName?: keyof JSX.IntrinsicElements;
  /** Props for logo wrapping Link component. If any link props defined, logo is wrapped with Link component. */
  linkProps?: LinkProps;
};

const TeliaFullLogo = () => (
  <svg
    fill="currentColor"
    fillRule="evenodd"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 760.71 300"
    className={styles['logo_svg']}
  >
    <path d="M455.19 105.91H408.9v130.4h-29.37v-130.4H332.8V80.54h122.39v25.37z" />
    <path d="M543.31 188.47H461.2c2.89 16.47 14.69 27.37 29.82 27.37 11.35 0 20.47-5.12 26.04-13.57l21.81 13.13c-11.13 14.91-29.37 23.81-47.84 23.81-30.48 0-56.96-24.25-56.96-58.97s26.26-58.74 55.63-58.74c38.48-.01 57.39 30.7 53.61 66.97zm-81.22-20.7h54.74c-3.34-14.46-12.68-23.59-27.15-23.59-13.57.01-23.81 9.35-27.59 23.59zM601.61 236.75c-4.23.89-8.68 1.11-12.68 1.11-19.58 0-28.26-9.35-28.26-28.26V80.54h27.37v125.28c0 9.57 5.79 10.9 13.57 10.01v20.92zM615.85 93.45c0-8.68 7.57-15.8 16.24-15.8 8.46 0 16.02 7.12 16.02 15.8s-7.57 15.8-16.02 15.8c-8.67 0-16.24-7.12-16.24-15.8zm2.45 142.86V125.05h27.37v111.26H618.3zM760.71 157.54v78.77h-25.59V223.4c-8.68 11.35-22.48 15.58-34.05 15.58-24.03 0-38.27-15.13-38.27-34.05 0-21.58 24.26-39.16 53.85-39.16 5.79 0 12.02.45 17.58 2.23v-7.34c0-11.13-6.45-16.91-19.58-16.91-11.79 0-22.25 6.01-28.7 13.8l-19.36-14.24c10.9-12.68 29.6-21.58 48.51-21.58 29.59-.02 45.61 12.89 45.61 35.81zm-26.48 35.82v-4.9c-4.45-1.56-9.12-2-14.91-2-20.03 0-30.26 8.01-30.26 17.8 0 8.01 6.9 13.13 17.36 13.13 13.79 0 27.81-8.23 27.81-24.03z" />
    <path d="M196.36 210.79c38.9-22.56 73.94-60.82 84.99-88.1 3.14-22.58 3.78-25.43 4.16-50.27.05-3.44-.16-6.74-.48-9.9-2.27 36.48-49.42 82.35-111.8 112.32-75.25 36.15-131.7 45.88-156.89 18.31 8.75 17.28 14.49 25.67 24.33 38.49 29.06 25.74 90.22 17.13 155.69-20.85z" />
    <path d="M164.46 148.99c70.98-34.24 120.42-82.95 111.8-115.24C262.11 9.31 233.47 0 202.76 0 111.73 0 0 60 0 136.55c0 9.84 2.17 20.84 6.06 32.37 20.76 27.55 84.24 15.84 158.4-19.93zM158.47 257.49c-49.92 20.24-78.49 13.79-94.28 1.17l-.23-.18c24.44 24.38 52.3 41.53 76.73 41.53 42.78 0 101.35-51.9 129.07-132.38-15.62 29.05-49.14 64.74-111.29 89.86z" />
  </svg>
);

const Logo: React.FunctionComponent<LogoProps> = ({
  size = 'md',
  color,
  tagName = 'div',
  linkProps,
}) => {
  const classNames = styles[`logo__size${upperFirst(size)}`];

  return (
    <Element className={classNames} tagName={tagName} color={color} inlineBlock>
      {linkProps ? (
        <Link {...linkProps}>
          <TeliaFullLogo />
        </Link>
      ) : (
        <TeliaFullLogo />
      )}
    </Element>
  );
};

export default Logo;
